import {
  BanknoteIcon,
  BarChart3,
  Calendar,
  ChevronDown,
  Copy,
  HandCoins,
  LayoutList,
  LogOut,
  Scale,
  Send,
  Settings,
  TowerControl,
  Wrench,
  PersonStanding,
  PowerSquareIcon,
  PhoneCall,
} from "lucide-react";
import React, { useState } from "react";
import Swal from "sweetalert2";
import AdminPanel from "./adminPanel.js";
import "./app.css";
import Database from "./db.js";
import Doubles from "./duplicates.js";
import LeadQue from "./leadsQue.js";
import Reports from "./reports.js";
import SendLeads from "./sendLeads.js";
import TaskCalendar from "./taskCalendar.js";
import LeadDetail from "./leadDetail.js"; // ← upewnij się że ścieżka poprawna

/* ─── nav config ─── */
const NAV_ITEMS = [
  {
    id: 2,
    label: "CHF",
    name: "CHF",
    icon: HandCoins,
    adminOnly: false,
    sell: false,
    depart: true,
  },
  {
    id: 8,
    label: "WIBOR",
    name: "WIBOR",
    icon: BanknoteIcon,
    adminOnly: false,
    sell: false,
    depart: true,
  },
  {
    id: 9,
    label: "SKD",
    name: "SKD",
    icon: Scale,
    adminOnly: false,
    sell: false,
    depart: true,
  },
  {
    id: 10,
    label: "SŁUŻEBNOŚĆ",
    name: "SLUZEBNOSC",
    icon: TowerControl,
    adminOnly: false,
    sell: false,
    depart: true,
  },
  {
    id: 12,
    label: "WSZYSTKIE",
    name: "ALL",
    icon: PowerSquareIcon,
    adminOnly: true,
    sell: false,
    depart: true,
  },
  {
    id: 13,
    label: "POŁ. PRZYCHODZĄCE",
    name: "con",
    icon: PhoneCall,
    adminOnly: false,
    sell: false,
    depart: false,
  },
  {
    id: 11,
    label: "SPRZEDAŻ",
    name: "SPRZEDAZ",
    icon: PersonStanding,
    adminOnly: false,
    sell: true,
    depart: true,
  },
  {
    id: 3,
    label: "ZADANIA",
    name: "ZADANIA",
    icon: Calendar,
    adminOnly: false,
    sell: false,
    depart: false,
  },
];

const NAV_TOOLS = [
  { id: 0, label: "KOLEJKA", icon: LayoutList, adminOnly: true, sell: false },
  { id: 1, label: "DUPLIKATY", icon: Copy, adminOnly: true, sell: false },
  {
    id: 7,
    label: "RAPORTY",
    icon: BarChart3,
    adminOnly: true,
    sell: false,
    restrictedTo: ["adrian@ftpeople.pl", "piotr@ftpeople.pl"],
  },
  {
    id: 6,
    label: "WYSYŁKA LEADÓW",
    icon: Send,
    adminOnly: true,
    restrictedTo: ["adrian@ftpeople.pl", "piotr@ftpeople.pl"],
  },
  { id: 4, label: "USTAWIENIA", icon: Settings, adminOnly: true, sell: false },
];

const ALL_ITEMS = [...NAV_ITEMS, ...NAV_TOOLS];

const PANELS = {
  0: LeadQue,
  1: Doubles,
  2: Database,
  8: Database,
  9: Database,
  10: Database,
  11: Database,
  12: Database,
  3: TaskCalendar,
  4: AdminPanel,
  6: SendLeads,
  7: Reports,
};

const SECRET_KEY = "FtPeople@)24!";

/* ─── helpers ─── */
const isVisible = (item, user) => {
  if (item.depart && user.role === "user" && !user.departs) return false;
  if (item.depart && user.role === "user" && !user.departs.includes(item.name))
    return false;
  if (item.sell && user.sell !== "1") return false;
  if (item.adminOnly && user.role !== "admin") return false;
  if (item.restrictedTo && !item.restrictedTo.includes(user.mail)) return false;
  return true;
};

/* ─── NavButton ─── */
const NavButton = ({ item, isActive, onClick, indent = false }) => {
  const Icon = item.icon;
  return (
    <button
      className={`nav-item ${isActive ? "active" : ""} ${
        indent ? "nav-item--indent" : ""
      }`}
      onClick={onClick}
    >
      <span className="nav-icon">
        <Icon size={indent ? 16 : 18} strokeWidth={isActive ? 2.2 : 1.8} />
      </span>
      <span className="nav-text">{item.label}</span>
      <span className="tooltip">{item.label}</span>
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════ */

const Main = ({ user }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [option, setOption] = useState(3);
  const [incomingLead, setIncomingLead] = useState(null);
  const [showIncomingLead, setShowIncomingLead] = useState(false);

  const visibleMain = NAV_ITEMS.filter((i) => isVisible(i, user));
  const visibleTools = NAV_TOOLS.filter((i) => isVisible(i, user));
  const toolIds = visibleTools.map((t) => t.id);
  const activeInTools = toolIds.includes(option);
  const showTools = toolsOpen || activeInTools;

  const ActivePanel = PANELS[option] || (() => null);
  const activeLabel = ALL_ITEMS.find((n) => n.id === option)?.label ?? "";

  const initials = (user.name || "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const closeIncoming = () => {
    setShowIncomingLead(false);
    setIncomingLead(null);
  };

  /* ─── Incoming call handler ─── */
  const handleIncomingCall = async () => {
    const { value: phone, isConfirmed } = await Swal.fire({
      title: "Połączenie przychodzące",
      html: `
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="#8c8ca1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.34 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z"/>
          </svg>
          <span style="font-size:13px;color:#8c8ca1;">Wpisz numer telefonu dzwoniącego</span>
        </div>
      `,
      input: "text",
      inputPlaceholder: "np. 600 123 456",
      inputAttributes: { maxlength: 20, autocomplete: "off" },
      showCancelButton: true,
      confirmButtonText: "Szukaj",
      cancelButtonText: "Anuluj",
      confirmButtonColor: "#ff8c00",
      cancelButtonColor: "#8c8ca1",
      background: "#ffffff",
      color: "#1a1a2e",
      customClass: {
        popup: "swal-crm-popup",
        title: "swal-crm-title",
        input: "swal-crm-input",
        confirmButton: "swal-crm-confirm",
        cancelButton: "swal-crm-cancel",
        actions: "swal-crm-actions",
      },
      inputValidator: (value) => {
        if (!value || value.trim().replace(/[\s\-]/g, "").length < 7)
          return "Podaj poprawny numer telefonu";
      },
    });

    if (!isConfirmed || !phone) return;

    Swal.fire({
      title: "Szukam w bazie…",
      allowOutsideClick: false,
      background: "#ffffff",
      color: "#1a1a2e",
      customClass: { popup: "swal-crm-popup", title: "swal-crm-title" },
      didOpen: () => Swal.showLoading(),
    });

    try {
      const normalized = phone
        .trim()
        .replace(/[\s\-]/g, "")
        .replace(/^\+?48/, "");

      const res = await fetch(
        `https://apiftpsplit.ftpeople.pl/api/leads/phone/${encodeURIComponent(
          normalized
        )}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: SECRET_KEY,
          },
        }
      );

      Swal.close();

      if (!res.ok) {
        await Swal.fire({
          icon: "warning",
          title: "Nie znaleziono",
          text: "Kontakt nie figuruje w bazie. Przeprowadź procedurę zgłoszenia.",
          confirmButtonText: "OK",
          confirmButtonColor: "#ff8c00",
          background: "#ffffff",
          color: "#1a1a2e",
          customClass: {
            popup: "swal-crm-popup",
            title: "swal-crm-title",
            confirmButton: "swal-crm-confirm",
          },
        });
        return;
      }

      const data = await res.json();

      if (!data || !data.id) {
        await Swal.fire({
          icon: "warning",
          title: "Nie znaleziono",
          text: "Kontakt nie figuruje w bazie. Przeprowadź procedurę zgłoszenia.",
          confirmButtonText: "OK",
          confirmButtonColor: "#ff8c00",
          background: "#ffffff",
          color: "#1a1a2e",
          customClass: {
            popup: "swal-crm-popup",
            title: "swal-crm-title",
            confirmButton: "swal-crm-confirm",
          },
        });
        return;
      }

      setIncomingLead(data);
      setShowIncomingLead(true);
    } catch {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "Błąd połączenia",
        text: "Nie udało się pobrać danych. Spróbuj ponownie.",
        confirmButtonText: "OK",
        confirmButtonColor: "#ff8c00",
        background: "#ffffff",
        color: "#1a1a2e",
        customClass: {
          popup: "swal-crm-popup",
          title: "swal-crm-title",
          confirmButton: "swal-crm-confirm",
        },
      });
    }
  };

  /* ─── nav click ─── */
  const handleNavClick = (itemId) => {
    if (itemId === 13) {
      handleIncomingCall();
      return;
    }
    setOption(itemId);
  };

  return (
    <div className="crm-layout">
      {/* ── Sidebar ── */}
      <aside className={`crm-sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">LS</div>
          <div className="sidebar-brand">
            <h1>Lead Splitter</h1>
            <span>by FTP</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-label">Menu</div>

          {visibleMain.map((item) => (
            <NavButton
              key={item.id}
              item={item}
              isActive={option === item.id}
              onClick={() => handleNavClick(item.id)}
            />
          ))}

          {user.role === "admin" && visibleTools.length > 0 && (
            <>
              <button
                className={`nav-item nav-group-toggle ${
                  activeInTools ? "active" : ""
                }`}
                onClick={() => setToolsOpen(!toolsOpen)}
              >
                <span className="nav-icon">
                  <Wrench size={18} strokeWidth={1.8} />
                </span>
                <span className="nav-text">NARZĘDZIA</span>
                <span
                  className={`nav-group-arrow ${
                    showTools ? "nav-group-arrow--open" : ""
                  }`}
                >
                  <ChevronDown size={14} />
                </span>
                <span className="tooltip">NARZĘDZIA</span>
              </button>

              <div
                className={`nav-submenu ${
                  showTools ? "nav-submenu--open" : ""
                }`}
              >
                {visibleTools.map((item) => (
                  <NavButton
                    key={item.id}
                    item={item}
                    isActive={option === item.id}
                    onClick={() => handleNavClick(item.id)}
                    indent
                  />
                ))}
              </div>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="avatar">{initials}</div>
          <div className="user-info">
            <p className="user-name">{user.name}</p>
            <p className="user-role">{user.role}</p>
          </div>
          <button
            className="logout-btn"
            onClick={() => window.location.reload(false)}
            title="Wyloguj"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="crm-main">
        <header className="crm-topbar">
          <h2>{activeLabel}</h2>
          <div className="topbar-right">
            <span className="topbar-badge">{user.role}</span>
          </div>
        </header>

        <main className="crm-content">
          <ActivePanel
            key={option}
            user={user}
            option={
              option === 2
                ? "CHF"
                : option === 8
                ? "WIBOR"
                : option === 9
                ? "SKD"
                : option === 10
                ? "SLUZEBNOSC"
                : option === 11
                ? "SPRZEDAZ"
                : option === 12
                ? "ALL"
                : ""
            }
          />
        </main>
      </div>

      {/* ── LeadDetail — połączenie przychodzące ── */}
      {showIncomingLead && incomingLead && (
        <div
          className="ld-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeIncoming();
          }}
        >
          <div className="ld-modal">
            <LeadDetail
              close={closeIncoming}
              lead={incomingLead}
              seller={incomingLead.sellerName || incomingLead.seller || ""}
              user={user}
              option={incomingLead.company || ""}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Main;
